/**
 * Deliverables — Phase 2a screen.
 *
 * Sections (§4 spec):
 *   - Header + progress ring ("{delivered} of {total} delivered")
 *   - Three labeled groups: Delivered / In progress / Upcoming
 *     • Delivered → amber Check icon + label
 *     • In progress → label + flowbar progress bar
 *     • Upcoming → muted dot + muted label
 *   - Status chips in IBM Plex Mono font (group labels)
 *
 * Data: GET /api/console/deliverables
 * Query key: ['deliverables']
 *
 * Visual system: "Site Office" (see src/styles/global.css) — .panel/.brk
 * bracketed cards, .lrow ledger rows, .flowbar progress, .stamp status tags.
 */
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api.js';
import Skeleton from '../components/Skeleton.jsx';
import ProgressRing from '../components/ProgressRing.jsx';
import { Check, AlertCircle } from 'lucide-react';

// ── Group config ──────────────────────────────────────────────────────────────
const GROUP_CONFIG = {
  delivered: {
    label: 'Delivered',
    /* Single-accent decision carried over from the prior design (amber, not
       success-green) — the amber check circle is already the completion
       signal, so the stamp/count chip stays on the same accent. */
    color: 'var(--acc-ink)',
  },
  in_progress: {
    label: 'In Progress',
    color: 'var(--acc-ink)',
  },
  upcoming: {
    label: 'Upcoming',
    color: 'var(--ink-soft)',
  },
};

// ── Individual item renderers ──────────────────────────────────────────────────

function DeliveredItem({ item }) {
  return (
    <div className="lrow">
      <div
        aria-hidden="true"
        style={{
          width: '22px',
          height: '22px',
          borderRadius: '50%',
          background: 'var(--acc)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Check size={12} color="var(--paper)" strokeWidth={3} aria-hidden="true" />
      </div>
      <span
        style={{
          fontSize: '14px',
          fontFamily: 'var(--font-body)',
          color: 'var(--ink)',
          lineHeight: 1.35,
          flex: 1,
        }}
      >
        {item.label}
      </span>
      <span className="stamp flat" style={{ color: 'var(--acc-ink)' }}>
        done
      </span>
    </div>
  );
}

function InProgressItem({ item }) {
  return (
    <div className="lrow" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div
          aria-hidden="true"
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: 'var(--acc)',
            flexShrink: 0,
          }}
        />
        <span
          style={{
            fontSize: '14px',
            fontFamily: 'var(--font-body)',
            color: 'var(--ink)',
            lineHeight: 1.35,
            flex: 1,
          }}
        >
          {item.label}
        </span>
        <span className="stamp" style={{ color: 'var(--acc-ink)' }}>
          in progress
        </span>
      </div>
      {/* Progress bar — signature "pipeline" flowbar */}
      <div className="flowbar" aria-hidden="true">
        <i />
      </div>
    </div>
  );
}

function UpcomingItem({ item }) {
  return (
    <div className="lrow" style={{ opacity: 0.75 }}>
      <div
        aria-hidden="true"
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          border: '1.5px solid var(--ink-soft)',
          background: 'transparent',
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontSize: '14px',
          fontFamily: 'var(--font-body)',
          color: 'var(--ink-soft)',
          lineHeight: 1.35,
          flex: 1,
        }}
      >
        {item.label}
      </span>
      <span className="stamp flat" style={{ color: 'var(--ink-soft)' }}>
        upcoming
      </span>
    </div>
  );
}

/**
 * DeliverablesGroup — labeled section with count stamp + item list.
 * variant: 'delivered' | 'in_progress' | 'upcoming'
 */
function DeliverablesGroup({ variant, items }) {
  if (!items || items.length === 0) return null;

  const cfg = GROUP_CONFIG[variant] || GROUP_CONFIG.upcoming;

  return (
    <div className="rv" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
        <h3 className="d-xl" style={{ fontSize: '16px' }}>
          {cfg.label}
        </h3>
        <span className="stamp-in stamp flat" style={{ color: cfg.color }}>
          {items.length}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {items.map((item) => {
          if (variant === 'delivered')   return <DeliveredItem   key={item.key} item={item} />;
          if (variant === 'in_progress') return <InProgressItem  key={item.key} item={item} />;
          return                                <UpcomingItem    key={item.key} item={item} />;
        })}
      </div>
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
function DeliverablesSkeleton() {
  return (
    <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div className="panel" style={{ position: 'relative', padding: '20px', display: 'flex', alignItems: 'center', gap: '20px' }}>
        <span className="brk brk-tl" aria-hidden="true" />
        <span className="brk brk-br" aria-hidden="true" />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Skeleton style={{ height: 28, width: '60%' }} />
          <Skeleton style={{ height: 16, width: '40%' }} />
        </div>
        <Skeleton style={{ width: '104px', height: '104px', borderRadius: '50%', flexShrink: 0 }} />
      </div>
      {[5, 2, 1].map((count, gi) => (
        <div key={gi} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Skeleton style={{ height: 20, width: '30%', marginBottom: '4px' }} />
          {Array.from({ length: count }).map((_, i) => (
            <Skeleton key={i} style={{ height: 52 }} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Error state ───────────────────────────────────────────────────────────────
function DeliverablesError({ error, refetch }) {
  return (
    <div
      style={{
        padding: '20px 16px',
        paddingTop: '64px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: '16px',
      }}
    >
      <div
        style={{
          width: '52px',
          height: '52px',
          borderRadius: '50%',
          background: 'var(--panel-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <AlertCircle size={24} color="var(--ink-soft)" strokeWidth={1.5} aria-hidden="true" />
      </div>
      <div>
        <p className="d-xl" style={{ fontSize: '20px', margin: '0 0 6px' }}>
          Couldn't load deliverables
        </p>
        <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: '14px', lineHeight: 1.5, fontFamily: 'var(--font-body)' }}>
          {error?.message || 'Something went wrong. Tap below to try again.'}
        </p>
      </div>
      <button type="button" className="btnx primary" onClick={() => refetch()}>
        Try again
      </button>
    </div>
  );
}

// ── Main screen ───────────────────────────────────────────────────────────────
export default function Deliverables() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['deliverables'],
    queryFn: () => apiFetch('/api/console/deliverables'),
  });

  if (isLoading) return <DeliverablesSkeleton />;
  if (error) return <DeliverablesError error={error} refetch={refetch} />;

  const { groups = {}, tier } = data;
  const delivered   = (groups.delivered   || []).length;
  const inProgress  = (groups.in_progress || []).length;
  const upcoming    = (groups.upcoming    || []).length;
  const total       = delivered + inProgress + upcoming;

  const tierLabels = {
    foundation:   'Foundation',
    lead_recovery: 'Lead Recovery',
    always_open:  'Always Open',
  };
  const tierLabel = tierLabels[tier] || tier || '';

  return (
    <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* ── Header: title + progress ring ────────────────────────────────── */}
      <div className="panel rv" style={{ position: 'relative', padding: '20px', display: 'flex', alignItems: 'center', gap: '20px' }}>
        <span className="brk brk-tl" aria-hidden="true" />
        <span className="brk brk-br" aria-hidden="true" />

        <div style={{ flex: 1, minWidth: 0 }}>
          <p className="mlabel" style={{ marginBottom: '8px' }}>
            Deliverables{tierLabel ? ` · ${tierLabel} tier` : ''}
          </p>
          <h1 className="d-xl" style={{ fontSize: '30px', marginBottom: '2px' }}>
            {delivered} <span className="serif-i" style={{ textTransform: 'none', color: 'var(--ink-soft)' }}>of</span> {total} delivered
          </h1>
        </div>

        <ProgressRing delivered={delivered} total={total} size={104} strokeWidth={8} />
      </div>

      {/* ── Three groups ──────────────────────────────────────────────────── */}
      <DeliverablesGroup variant="delivered"   items={groups.delivered} />
      <DeliverablesGroup variant="in_progress" items={groups.in_progress} />
      <DeliverablesGroup variant="upcoming"    items={groups.upcoming} />
    </div>
  );
}
